import { defineFilterConfig, filterFieldAdapters } from "@src/components/business-form";
import { StockKanbanComplectState, type StockKanbanContainerFilter } from "../../../../../../api/stockKanbanContainers";

/** Значения формы фильтра списка контейнеров КАНБАН. */
export interface StockKanbanSupplyFilterFormValues {
    /** Код подразделения-потребителя (цех / кладовая). */
    divisionCode: number | null;
    /** Код участка-потребителя; `null` — без ограничения по участку. */
    sectionCode: number | null;
    /** Отбирать контейнеры только по текущему пользователю. */
    onlyCurrentUser: boolean;
}

/**
 * Конфигурация фильтра списка контейнеров КАНБАН. \
 * Чекбокс `onlyCurrentUser` маппится в wire-поле `ComplectUserCodes` в {@link StockKanbanSupplyFilterForm}
 * (нужен код текущего пользователя из session store).
 */
export const stockKanbanSupplyFilterFormConfig = defineFilterConfig<
    StockKanbanSupplyFilterFormValues,
    StockKanbanContainerFilter
>({
    fields: {
        divisionCode: {
            type: "number",
            nullable: true,
            label: "Подразделение-потребитель",
            // validation: { required: true },
            map: filterFieldAdapters.singleCodeArray("DivisionCodes"),
        },
        sectionCode: {
            type: "number",
            nullable: true,
            label: "Участок-потребитель",
            /**
             * Зависит от подразделения-потребителя: без выбранного подразделения участок недоступен. \
             * TODO(forms/spp-lib): заменить на `dependsOn` (+ clearOnChange) в shared form engine;
             * сейчас clear — ручной `onChange` в {@link StockKanbanSupplyFilterForm}.
             */
            disabled: (values) => values.divisionCode == null || values.divisionCode === 0,
            map: filterFieldAdapters.singleCodeArray("SectionCodes"),
        },
        onlyCurrentUser: {
            type: "boolean",
            label: "Отбирать контейнеры только по текущему пользователю",
            default: true,
            /**
             * В domain-фильтр чекбокс сам по себе не пишется: \
             * `ComplectUserCodes` собирается в форме с кодом текущего пользователя.
             */
            map: {
                from: (domain) => (domain.ComplectUserCodes?.length ?? 0) > 0,
                to: () => ({}),
            },
        },
    },
});

/**
 * Собирает wire-фильтр списка: подразделение, участок, статусы комплектования и опционально текущий пользователь.
 * @param values значения формы
 * @param currentUserCode код текущего пользователя; при `onlyCurrentUser` обязателен
 */
export function toStockKanbanSupplyFilter(
    values: StockKanbanSupplyFilterFormValues,
    currentUserCode: number | null,
): StockKanbanContainerFilter {
    const filter: StockKanbanContainerFilter = {
        ...stockKanbanSupplyFilterFormConfig.toDomain(values),
        ComplectStates: [
            StockKanbanComplectState.Idle,
            StockKanbanComplectState.InProgress,
            StockKanbanComplectState.Finished,
        ],
    };

    if (values.onlyCurrentUser && currentUserCode != null && currentUserCode !== 0) {
        filter.ComplectUserCodes = [currentUserCode];
    }

    return filter;
}
